// NOTE: react-query and constants imports exist later in this file; avoid duplicate declarations

import { getModelDetailsQueryKey } from '@/app/components/features/evaluations/Summary/helpers';
import { toastManager } from '@/app/ui/components/toast';
import {
  analyseDocuments,
  deleteTenderPackage,
  getTenderPackageDetails,
  updateTenderPackage,
} from '@/app/services/tenderPackagesService';
import { UpdateTenderPackageRequest } from '@/app/types/Dtos/request';
import { GetTenderPackgeDetailsResponse } from '@/app/types/Dtos/response';
import { TenderPackage } from '@/app/types/models';
import { SummarizationNamespaces, mutationKeys, queryKeys } from '@/app/utils/constants';
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'use-intl';
import { useParams } from '@tanstack/react-router';

export const getTenderPackageDetailsQuery = (
  tenderPackageId: string,
  initialData?: GetTenderPackgeDetailsResponse,
) =>
  queryOptions({
    staleTime: 600 * 1000,
    queryKey: [queryKeys.TENDER_PACKAGE_DETAILS, tenderPackageId],
    queryFn: async () => {
      const { data } = await getTenderPackageDetails(tenderPackageId);
      return data;
    },
    refetchIntervalInBackground: true,
    initialData,
  });

export const useAnalyseRfpDocs = () => {
  const t = useTranslations('general.notifications');

  return useMutation({
    mutationKey: [mutationKeys.ANALYSE_RFP_DOCUMENTS],
    mutationFn: async (tenderPackageId: string) => {
      const res = await analyseDocuments(tenderPackageId);
      return { res, tenderPackageId };
    },
    onError: () => {
      toastManager.add({
        type: 'error',
        title: t('errorAnalysisRedirecting'),
      });
    },
  });
};

export const useUpdateTenderPackage = () => {
  const { tenderPackageId } = useParams({
    strict: false,
  });
  const queryClient = useQueryClient();
  const t = useTranslations('general.notifications');
  if (!tenderPackageId) {
    throw new Error('tenderPackageId param is required for useUpdateTenderPackage');
  }

  return useMutation({
    mutationFn: async (payload: UpdateTenderPackageRequest) => {
      return await updateTenderPackage(payload, tenderPackageId);
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: getModelDetailsQueryKey(SummarizationNamespaces.TENDER_PACKAGE, tenderPackageId),
      });
      toastManager.add({
        type: 'success',
        title: t('updatedSuccessfully'),
      });
    },
  });
};

export const useDeleteTenderPackage = (
  props: { tenderPackageId?: string; onSuccess?: () => void } = {},
) => {
  const queryClient = useQueryClient();
  const t = useTranslations('general.notifications');

  return useMutation({
    mutationFn: async (tenderPackageId: string) => {
      return await deleteTenderPackage(tenderPackageId);
    },
    onSuccess: async (_, variables) => {
      const previousData = queryClient.getQueryData<TenderPackage[]>([queryKeys.TENDER_PACKAGES]);
      queryClient.setQueryData(
        [queryKeys.TENDER_PACKAGES],
        previousData?.filter((tp) => tp.id !== variables),
      );
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.TENDER_PACKAGES],
      });
      props?.onSuccess?.();
      toastManager.add({
        type: 'success',
        title: t('deletedSuccessfully'),
      });
    },
  });
};
